import { debounce } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TextField, InputAdornment, Box, IconButton, Button, Tooltip } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { useIsMobile } from '@carcareers/utils';
import { useDataGrid } from './DataGridContext';

type Func = () => void;

interface DataGridSearchProps {
  isExpanded?: boolean;
  onToggleExpanded?: (expanded: boolean) => void;
}

export const DataGridSearch: FC<DataGridSearchProps> = ({ isExpanded: externalExpanded, onToggleExpanded }) => {
  const { queryParams, setQueryParams } = useDataGrid();
  const [value, setValue] = useState(queryParams.q || '');
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isMobile = useIsMobile();
  const ref = useRef<Func>();

  const isExpanded = externalExpanded !== undefined ? externalExpanded : internalExpanded;
  const setExpanded = onToggleExpanded || setInternalExpanded;

  const onChange = useCallback(() => {
    setQueryParams({ ...queryParams, q: value });
  }, [value, queryParams, setQueryParams]);

  const debouncedCallback = useMemo(() => {
    const func = () => {
      ref.current?.();
    };

    return debounce(func, 500);
  }, []);

  const handleClearSearch = () => {
    setValue('');
    setQueryParams({ ...queryParams, q: '' });
  };

  const handleToggleExpanded = () => {
    setExpanded(!isExpanded);
  };

  const handleClose = () => {
    setExpanded(false);
  };

  useEffect(() => {
    ref.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setValue(queryParams.q || '');
  }, [queryParams.q]);

  useEffect(() => {
    return () => {
      debouncedCallback.cancel();
    };
  }, [debouncedCallback]);

  if (isMobile && !isExpanded) {
    return (
      <IconButton
        onClick={handleToggleExpanded}
        sx={{
          color: 'text.secondary',
          '&:hover': {
            color: 'text.primary',
          },
        }}
      >
        <SearchIcon />
      </IconButton>
    );
  }

  return (
    <>
      <TextField
        placeholder="Search"
        variant="standard"
        value={value}
        onChange={(e) => {
          debouncedCallback();
          setValue(e.target.value);
        }}
        sx={{
          width: isMobile && isExpanded ? '100%' : '350px',
        }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Clear search">
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {value && (
                      <Box
                        component="button"
                        type="button"
                        onClick={handleClearSearch}
                        sx={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              </InputAdornment>
            ),
          },
        }}
      />
      {isExpanded && (
        <Button onClick={handleClose} variant="text" sx={{ ml: 1, height: '40px' }}>
          Close
        </Button>
      )}
    </>
  );
};
